import { Timestamp } from "firebase/firestore";

export const ImportedInvestorCollection = "imported-investors";

// Shared Enums

export enum InvestorRole {
  VC = "vc",
  ANGEL = "angel",
  FUND = "fund",
  ACCELERATOR = "accelerator",
  INCUBATOR = "incubator",
  CORPORATE = "corporate",
  FAMILY_OFFICE = "family_office",
  OTHER = "other",
}

export type CurrencyAmount = {
  value: number;
  currency?: string; // default USD
};

// Sub-objects

export type CheckSize = {
  min?: CurrencyAmount;
  max?: CurrencyAmount;
  bucket?: string; // "10-50k", "1-5 M", etc.
};

export type GeographyFocus = {
  countries?: string[]; // ["US", "CAN", "DE"]
  regions?: string[]; // ["LATAM", "MENA"]
  cities?: string[]; // ["San Francisco", "Berlin"]
};

export type Investment = {
  company: string;
  industry?: string;
  round?: string; // "Seed", "Series A"…
  amount?: CurrencyAmount;
  preMoneyValuation?: CurrencyAmount;
  announcedDate?: Timestamp;
  lead?: boolean;
};

export type Firm = {
  id?: string; // generated if you normalise firms later
  name: string;
  website?: string;
  logo?: string;
  description?: string;
  foundingYear?: number;
  fundType?: string; // "VC Fund", "Syndicate", …
  fundStage?: string[]; // "Seed → Series B"
  fundFocus?: string[]; // sector focus at firm level
};

// Provenance & Data-Quality

export type MetadataPair = {
  tag: string;
  value: string;
  type?: string;
};

export type FieldStatus = {
  field: string;
  status: "verified" | "unverified" | "disputed" | "incorrect" | "outdated";
};

// Main Import Record

export type ImportedInvestorEntry = {
  // Identity
  id: string;
  firstName: string | null;
  lastName: string | null;
  fullName: string | null;
  photos: string[];

  // Contact
  email: string | null;
  phone: string | null;
  social: {
    linkedin: string | null;
    twitter: string | null;
    crunchbase: string | null;
    instagram: string | null;
    youtube: string | null;
    tiktok: string | null;
    other: string[];
  };

  // Role + Affiliation
  title: string | null; // "General Partner"
  investorRole: InvestorRole | null; // VC, Angel, …
  primaryFirm: Firm | null;
  otherFirms: Firm[];

  // Thesis & Preferences
  stageFocus: string[]; // ["Pre-Seed", "Seed"]
  sectorFocus: string[]; // ["Fintech", "Climate"]
  geographyFocus: GeographyFocus | null;
  checkSize: CheckSize | null;
  leadsRounds: boolean | null;

  // Portfolio
  selectedInvestments: Investment[];
  portfolioCompanies: string[]; // fallback when only names are available

  // Meta
  tags: string[];
  notes: string | null;
  otherMetadata: MetadataPair[];

  // Import Bookkeeping
  sourceName: string; // CSV / Airtable name
  sourceUrl: string | null;
  importedAt: Timestamp;
  importedByUserId: string;

  // Lifecycle & Quality
  status: "active" | "rejected" | "pending";
  fieldStatuses: FieldStatus[];
  updatedAt: Timestamp | null;
  searchableInvestorEntryId: string | null;
};
